import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';

interface CreateAlbumData {
   title: string;
   description: string;
   photoIds: string[];
   type?: 'private' | 'public' | 'paid';
   enableParticipantParticipation?: boolean;
   albumDate?: string;
}

interface AlbumIntegrationOptions {
   tripId?: string;
   eventId?: string;
   userId: string;
}

export const useAlbumIntegration = ({ tripId, eventId, userId }: AlbumIntegrationOptions) => {
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const navigate = useNavigate();

   const createAlbumFromPhotos = useCallback(
      async (albumData: CreateAlbumData) => {
         setLoading(true);
         setError(null);

         try {
            const { getAuthHeaders } = await import('../utils/auth');
            const headers = await getAuthHeaders();

            console.log('🎨 useAlbumIntegration: Creating album with data:', {
               ...albumData,
               createdBy: userId,
               tripId: tripId || null,
               eventId: eventId || null,
            });

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/create`, {
               method: 'POST',
               headers,
               body: JSON.stringify({
                  title: albumData.title,
                  description: albumData.description || '',
                  createdBy: userId,
                  type: albumData.type || 'private',
                  enableParticipantParticipation: albumData.enableParticipantParticipation ?? true,
                  tripId: tripId || null,
                  eventId: eventId || null,
                  photoIds: albumData.photoIds,
                  source: tripId ? 'trip' : eventId ? 'event' : 'manual',
                  tags: [...(tripId ? ['trip'] : []), ...(eventId ? ['event'] : []), 'photos'],
                  albumDate: albumData.albumDate || null,
               }),
            });

            console.log('📡 useAlbumIntegration: Create album response status:', response.status);

            if (!response.ok) {
               // Check if response is JSON or HTML
               const contentType = response.headers.get('content-type');
               let errorMessage = 'Failed to create album';
               
               if (contentType && contentType.includes('application/json')) {
                  const errorData = await response.json();
                  errorMessage = errorData.message || errorMessage;
               } else {
                  // Response is HTML (error page)
                  const errorText = await response.text();
                  console.error('❌ HTML error response:', errorText.substring(0, 200));
                  errorMessage = `Server error (${response.status}): ${response.statusText}`;
               }
               
               throw new Error(errorMessage);
            }

            const responseData = await response.json();
            console.log('✅ useAlbumIntegration: Album created successfully:', responseData);

            // Navigate to the new album
            navigate(`/albums/${responseData.album.id}`);

            return responseData;
         } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred';
            console.error('💥 useAlbumIntegration: Error creating album:', err);
            setError(errorMessage);
            throw err;
         } finally {
            setLoading(false);
         }
      },
      [tripId, eventId, userId, navigate]
   );

   const linkExistingAlbum = useCallback(
      async (albumId: string) => {
         setLoading(true);
         setError(null);

         try {
            const { getAuthHeaders } = await import('../utils/auth');
            const headers = await getAuthHeaders();

            const endpoint = tripId
               ? `${process.env.REACT_APP_API_URL}/api/trips/${tripId}/link-album`
               : `${process.env.REACT_APP_API_URL}/api/events/${eventId}/link-album`;

            const response = await fetch(endpoint, {
               method: 'POST',
               headers,
               body: JSON.stringify({ albumId }),
            });

            if (!response.ok) {
               const errorData = await response.json();
               throw new Error(errorData.message || 'Failed to link album');
            }

            return await response.json();
         } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred';
            setError(errorMessage);
            throw err;
         } finally {
            setLoading(false);
         }
      },
      [tripId, eventId]
   );

   const unlinkAlbum = useCallback(
      async (albumId: string) => {
         setLoading(true);
         setError(null);

         try {
            const { getAuthHeaders } = await import('../utils/auth');
            const headers = await getAuthHeaders();

            const endpoint = tripId
               ? `${process.env.REACT_APP_API_URL}/api/trips/${tripId}/unlink-album`
               : `${process.env.REACT_APP_API_URL}/api/events/${eventId}/unlink-album`;

            const response = await fetch(endpoint, {
               method: 'POST',
               headers,
               body: JSON.stringify({ albumId }),
            });

            if (!response.ok) {
               const errorData = await response.json();
               throw new Error(errorData.message || 'Failed to unlink album');
            }

            return await response.json();
         } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred';
            setError(errorMessage);
            throw err;
         } finally {
            setLoading(false);
         }
      },
      [tripId, eventId]
   );

   const getLinkedAlbums = useCallback(
      async (userEmail?: string) => {
         setLoading(true);
         setError(null);

         try {
            const { getAuthHeaders } = await import('../utils/auth');
            const headers = await getAuthHeaders();

            const endpoint = tripId
               ? `${process.env.REACT_APP_API_URL}/api/trips/${tripId}/albums`
               : `${process.env.REACT_APP_API_URL}/api/events/${eventId}/albums`;

            // Add userId and userEmail as query parameters
            const url = new URL(endpoint);
            url.searchParams.append('userId', userId);
            if (userEmail) {
               url.searchParams.append('userEmail', userEmail);
            }

            console.log('🌐 useAlbumIntegration: Making API call to:', url.toString());
            console.log('🌐 useAlbumIntegration: Request details:', {
               tripId,
               eventId,
               userId,
               userEmail,
            });

            const response = await fetch(url.toString(), {
               headers,
            });
            console.log('📡 useAlbumIntegration: Response status:', response.status, response.ok);

            if (!response.ok) {
               const errorData = await response.json();
               console.error('❌ useAlbumIntegration: API Error:', errorData);
               throw new Error(errorData.message || 'Failed to fetch linked albums');
            }

            const result = await response.json();
            console.log('✅ useAlbumIntegration: API Success:', result);

            // Handle both direct array response and wrapped response
            if (Array.isArray(result)) {
               console.log(
                  '✅ useAlbumIntegration: Direct array response, returning:',
                  result.length,
                  'albums'
               );
               return result;
            } else if (result.data && Array.isArray(result.data)) {
               console.log(
                  '✅ useAlbumIntegration: Wrapped response, returning:',
                  result.data.length,
                  'albums'
               );
               return result.data;
            } else {
               console.log('❌ useAlbumIntegration: Unexpected response format:', result);
               return [];
            }
         } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An error occurred';
            console.error('💥 useAlbumIntegration: Error:', err);
            setError(errorMessage);
            throw err;
         } finally {
            setLoading(false);
         }
      },
      [tripId, eventId, userId]
   );

   return {
      createAlbumFromPhotos,
      linkExistingAlbum,
      unlinkAlbum,
      getLinkedAlbums,
      loading,
      error,
      clearError: () => setError(null),
   };
}; 